import React from 'react';
import { Type, Trash2, LayoutTemplate, BookOpen } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели со стандартными размерами текста и сохраненными пресетами
 */
const CommonSizesSidebar = ({ 
  savedPresets, 
  deleteSavedPreset, 
  loadSavedPreset 
}) => {
  // Стандартные размеры текста с описаниями для разных устройств и контекстов
  const commonSizes = [
    {
      title: 'Основной текст веб-страницы',
      fontSize: '16px',
      lineHeight: '1.5',
      description: 'Стандартный размер для комфортного чтения на экране компьютера или ноутбука.'
    },
    {
      title: 'Заголовок H1',
      fontSize: '32px',
      lineHeight: '1.2',
      description: 'Главный заголовок страницы, обычно один на страницу.'
    },
    {
      title: 'Заголовок H2',
      fontSize: '24px',
      lineHeight: '1.3',
      description: 'Подзаголовок для разделения крупных разделов контента.'
    },
    {
      title: 'Мобильный текст',
      fontSize: '14px',
      lineHeight: '1.4',
      description: 'Оптимизированный размер для мобильных устройств.'
    },
    {
      title: 'Версия для слабовидящих',
      fontSize: '18px',
      lineHeight: '1.8',
      description: 'Соответствует ГОСТ Р 57850-2017 для веб-сайтов.'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <LayoutTemplate className="w-5 h-5 mr-2 text-indigo-500" />
        Стандартные размеры
      </h3>
      
      {/* Стандартные размеры */}
      <div className="space-y-3 mb-6">
        {commonSizes.map((size, index) => (
          <div 
            key={index} 
            className="bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
          >
            <div className="flex justify-between items-start">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white leading-tight">
                {size.title}
              </h4>
              <div className="px-2 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded">
                {size.fontSize}
              </div>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 mb-2">
              {size.description}
            </p>
            <div className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 p-2 rounded">
              <div
                style={{
                  fontSize: size.fontSize,
                  lineHeight: size.lineHeight,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                Пример текста
                </div>
            </div>
          </div>
        ))}
      </div>

      {/* Сохраненные пресеты пользователя */}
      <div className="mb-2">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
          <BookOpen className="w-4 h-4 mr-2 text-indigo-500" />
          Сохраненные пресеты
        </h4>
        
        {savedPresets.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
            У вас пока нет сохраненных пресетов. Настройте параметры текста и нажмите "Сохранить пресет".
          </p>
        ) : (
          <div className="space-y-2">
            {savedPresets.map((preset) => (
              <div 
                key={preset.id} 
                className="bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
              >
                <div className="flex justify-between items-center">
                  <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                    {preset.name}
                  </h5>
                  <div className="flex items-center space-x-1">
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => loadSavedPreset(preset.id)}
                      title="Загрузить пресет"
                    >
                      <Type className="w-3.5 h-3.5 text-indigo-500" />
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => deleteSavedPreset(preset.id)}
                      title="Удалить"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 grid grid-cols-2 gap-x-2 mt-1">
                  <div>Размер: {preset.formData.fontSize}{preset.formData.fontSizeUnit}</div>
                  <div>Интервал: {preset.formData.lineHeight}</div>
                </div>
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 p-2 rounded">
                  <div
                    style={{
                      fontFamily: preset.formData.fontFamily,
                      fontSize: `${preset.formData.fontSize}${preset.formData.fontSizeUnit}`,
                      lineHeight: preset.formData.lineHeight,
                      letterSpacing: `${preset.formData.letterSpacing}em`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Пример текста
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Нормативная информация */}
      <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <div className="flex items-start text-sm">
          <Type className="w-5 h-5 mr-3 text-indigo-500 mt-0.5" />
          <div>
            <p className="text-indigo-700 dark:text-indigo-300">
              <span className="font-medium">Стандартные размеры шрифтов в России</span>
            </p>
            <p className="text-indigo-600 dark:text-indigo-400 text-xs mt-1">
              Для печатной продукции согласно ГОСТ 5773-90, электронных изданий по ГОСТ Р 54723-2019 и веб-сайтов по ГОСТ Р 57850-2017.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonSizesSidebar;